import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Button,
  Box,
  Typography,
  IconButton,
  Checkbox
} from '@mui/material';
import AndroidIcon from '@mui/icons-material/Android';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { deleteCategory, deleteKind, getAntiPassbackReset, antiPassbackResetSet, deleteUnitOfMeasure, deleteArticle, deleteOrganizationUnit, deleteEmployee, deleteItem, deleteReader, openBarrier, getCountExits, countExit, soundSupport, getOrgranizationalUnits, getUnitsOfMeasure, deleteIngredient, getAndroidLogById } from '../../api/axios';
import { hasPermission, getUsername } from '../../context/AuthFunc';
import { PERMISSIONS } from '../../constants/const';

const DataTable = ({ initialData, columns, entityName, h2 }) => {
  const { t } = useTranslation('global');
  const [data, setData] = useState(initialData);
  const [antiPassbackReset, setAntiPassbackReset] = useState(false);
  const [countExits, setCountExits] = useState(false);
  const [organizationalUnits, setOrganizationalUnits] = useState([]);
  const [unitsOfMeasure, setUnitsOfMeasure] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    fetchCount();
    fetchAntiPassbackReset();
    fetchOrganzizationUnits();
    fetchUnitsOfMeasure();

  }, []);

  const handleEdit = (id) => {
    navigate(`/${entityName}/edit/${id}`);
  };
  const fetchAntiPassbackReset = async () => {
    const res = await getAntiPassbackReset();
    setAntiPassbackReset(res);
    return res;
  }
  const fetchCount = async () => {
    const res = await getCountExits();
    setCountExits(res);
    return res;
  }
  const fetchOrganzizationUnits = async () => {
    const res = await getOrgranizationalUnits();
    setOrganizationalUnits(res);
    return res;
  }

  const fetchUnitsOfMeasure = async () => {
    const res = await getUnitsOfMeasure();
    setUnitsOfMeasure(res);
    return res;
  }
  const handleDelete = async (id) => {
    if (window.confirm(t('table.areYouSure'))) {
      const params = new URLSearchParams();
      params.append("id", id);

      let result;
      if (entityName === "readers") {
        result = await deleteReader(params);
      } else if (entityName === "employees") {
        result = await deleteEmployee(id);
      } else if (entityName == "organization-units") {
        result = await deleteOrganizationUnit(id);
      } else if (entityName == "articles") {
        result = await deleteArticle(id);
      } else if (entityName == "units-of-measure") {
        result = await deleteUnitOfMeasure(id);
      } else if (entityName == "ingredients") {
        result = await deleteIngredient(id);
      } else if (entityName == "price-list") {
        result = await deleteItem(params);
      } else if (entityName == "categories") {
        result = await deleteCategory(params);
      } else if (entityName == "kinds") {
        result = await deleteKind(params);
      }
      console.log(result);
      if (result?.success) {
        // Filtriraj stavke kako bi uklonio obrisanu stavku
        setData((prevData) => prevData.filter((item) => item.id !== id));
        console.log(t('table.deletedSuccessfully'));
      } else {
        console.error(t('table.deleteFailed'));
      }
    }
  };

  const handleOpen = async (id) => {
    const reader = data.find((item) => item.id === id);
    if (reader) {
      const params = new URLSearchParams();
      params.append("command", "OPEN_BARRIER");
      params.append("serial_number", reader.serial_num);
      const username = getUsername();
      if (username) {
        params.append("event_type", username);
      }
      try {
        const res = await openBarrier(params);
        console.log(res);
      } catch (error) {
        console.error('Error opening barrier:', error);
      }
    } else {
      alert(t('table.readerNotFound'));
    }
  };

  // handleSound: Connects to the remote WebSocket server for audio streaming (WebRTC)
  const handleSound = async (id) => {
    const deviceId = "browser-123";
    const targetDeviceId = id; // ili uneti dinamički kasnije
    const ws = new WebSocket("wss://94.130.188.86:9000");

    let pc;

    ws.onopen = async () => {
      console.log("✅ WebSocket connected");

      // 1. Registruj klijenta
      const registerMsg = {
        type: "REGISTER",
        deviceId,
      };
      ws.send(JSON.stringify(registerMsg));
      console.log("📤 Sent REGISTER");

      // 2. Uzmi mikrofon
      const localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });

      // 3. WebRTC konekcija
      pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
      });

      // 4. Dodaj audio u peer connection
      localStream.getTracks().forEach(track => pc.addTrack(track, localStream));

      // 5. Pošalji ICE kandidate serveru
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          const iceMsg = {
            type: "ice-candidate",
            candidate: event.candidate,
            deviceId,
            targetDeviceId,
          };
          ws.send(JSON.stringify(iceMsg));
          console.log("📤 Sent ICE candidate");
        }
      };

      // 6. Napravi i pošalji offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const offerMsg = {
        type: "offer",
        sdp: offer.sdp,
        deviceId,
        targetDeviceId,
      };
      ws.send(JSON.stringify(offerMsg));
      console.log("📤 Sent offer");
    };

    // 7. Poruke koje dolaze sa servera
    ws.onmessage = async (event) => {
      const message = JSON.parse(event.data);
      console.log("📩 Received:", message);

      if (message.type === "answer") {
        await pc.setRemoteDescription(new RTCSessionDescription({ type: "answer", sdp: message.sdp }));
        console.log("✅ Set remote description from answer");
      }

      if (message.type === "ice-candidate") {
        await pc.addIceCandidate(new RTCIceCandidate(message.candidate));
        console.log("✅ Added remote ICE candidate");
      }
    };

    ws.onerror = (error) => {
      console.error("❌ WebSocket error", error);
    };

    ws.onclose = () => {
      console.log("🔌 WebSocket closed");
    };
  };

  const handleExits = async (checked) => {
    let params = new URLSearchParams();
    let res;
    if (checked) {
      params.append('value', '1');
      res = await countExit(params);
      setCountExits(true)
    }
    else {
      params.append('value', '0');
      res = await countExit(params);
      setCountExits(false)
    }
    console.log(countExits)
  };
  const handleAntiPassbackReset = async (checked) => {
    let params = new URLSearchParams();
    let res;
    if (checked) {
      params.append('value', '1');
      res = await antiPassbackResetSet(params);
      setAntiPassbackReset(true)
    }
    else {
      params.append('value', '0');
      res = await antiPassbackResetSet(params);
      setAntiPassbackReset(false)
    }
    console.log(antiPassbackReset)
  };

  const handleOpenAll = async () => {
    for (const item of data) {
      await handleOpen(item.id);
    }
  };

  const handleAndroidLogs = async (id) => {
    getAndroidLogById(id);
  }
  return (
    <Box fullWidth>
      <Typography variant="h4" gutterBottom>
        {h2}
      </Typography>
      <TableContainer
        component={Paper}
        fullWidth
      >
        <Table
          fullWidth
        >
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.accessor} variant="head">
                  {column.Header}
                </TableCell>
              ))}
              {!(entityName === "templateGroupEvents") && (
                <TableCell variant="head">{t("table.actions")}</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item) => (
              <TableRow key={item.id}>
                {columns.map((column) => (
                  <TableCell key={column.accessor}>
                    {column.accessor === "active" || column.accessor === "anti_passback" || column.accessor === "special_category"
                      ? Number(item[column.accessor]) === 1
                        ? t("table.yes")
                        : t("table.no")
                      : column.accessor === "organization_unit_id"
                        ? organizationalUnits.find(unit => unit.id === item[column.accessor])?.name || item[column.accessor]
                        : column.accessor === "unit_of_measure_id"
                          ? unitsOfMeasure.find(unit => unit.id === item[column.accessor])?.name || item[column.accessor]
                          : column.accessor === "allowed_days" && entityName === "kinds" && item[column.accessor]
                            ? item[column.accessor].split(',').map(day => t(`days.${['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][day - 1]}`)).join(', ')
                            : item[column.accessor]}
                  </TableCell>

                ))}
                {!(entityName === "templateGroupEvents") && (
                  <TableCell>
                    <IconButton
                      color="inherit"
                      onClick={() => handleEdit(item.id)}
                      aria-label="edit"
                    >
                      <EditIcon />
                    </IconButton>
                    {hasPermission(PERMISSIONS.DELETE_ITEM) && (<IconButton
                      color="inherit"
                      onClick={() => handleDelete(item.id)}
                      aria-label="delete"
                    >
                      <DeleteIcon />
                    </IconButton>)}
                    {(entityName === "readers") && (
                      <IconButton
                        color="inherit"
                        onClick={() => handleOpen(item.id)}
                        aria-label="open"
                      >
                        <LockOpenIcon />
                      </IconButton>
                    )}
                    {(entityName === "readers") && (
                      <IconButton
                        color="inherit"
                        onClick={() => handleSound(item.id)}
                        aria-label="open"
                      >
                        <VolumeUpIcon />
                      </IconButton>
                    )}
                    {(entityName === "readers") && (
                      <IconButton color="inherit" onClick={() => handleAndroidLogs(item.serial_num)} aria-label='open'>
                        <AndroidIcon />
                      </IconButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {(entityName === "readers") && (
        <Box width="100%" display="flex" justifyContent="flex-end" mt={4} mb={4}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenAll}
            startIcon={<LockOpenIcon />}
          >
            {t("table.openAll")}
          </Button>
          <Button
            sx={{ marginLeft: 2 }}
            variant="contained"
            color="primary"
            onClick={() => { window.location.href = "/logs" }}
          >
            {t("table.showLogs")}
          </Button>
        </Box>
      )}
      {(entityName === "readers") && (
        <>
          <Box width="100%" display="flex" justifyContent="flex-end" alignItems="center" mt={4} mb={4}>
            <Typography>
              {t('table.exits')}
            </Typography>
            <Checkbox
              onChange={(e) => handleExits(e.target.checked)}
              checked={countExits}
            />
          </Box>
          <Box width="100%" display="flex" justifyContent="flex-end" alignItems="center" mt={4} mb={4}>
            <Typography>
              {t('table.anti-passback-reset')}
            </Typography>
            <Checkbox
              onChange={(e) => handleAntiPassbackReset(e.target.checked)}
              checked={antiPassbackReset}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default DataTable;
